import * as _ from 'lodash';

import { SNIPPET_GROUPING } from '../../../../../shared/featureFlags/flagNames';
import { useQuery as useQueryParams, useParams as useRouteParams } from '../../../../contexts/RouterContext';
import { useIsFeatureEnabled } from '../../../../helpers/features';
import { omitClientOnlySnippetParams, type SnippetsRouteParams } from '../../../../hooks/api/snippets/shared';
import {
  getSnippetPackages,
  getSnippetPackagesQueryKey,
  type UseSnippetPackagesOptions,
} from '../../../../hooks/api/snippets/useSnippetPackages';
import { getSnippets, getSnippetsQueryKey, type UseSnippetsOptions } from '../../../../hooks/api/snippets/useSnippets';
import { useQuery } from '../../../../ReactQuery';
import type { SnippetPackagesResponse, SnippetsQueryParams, SnippetsResponse } from '../../../../../shared/snippets';

import useSnippetGrouping from './useSnippetGrouping';

export type UseSnippetsOrSnippetPackagesOptions = UseSnippetsOptions | UseSnippetPackagesOptions;

function getSnippetsOrSnippetPackagesQueryKey(
  isGroupingByPackage: boolean,
  options: UseSnippetsOrSnippetPackagesOptions,
) {
  return isGroupingByPackage
    ? getSnippetPackagesQueryKey(options)
    : getSnippetsQueryKey(options);
}

/**
 * useSnippetsOrSnippetPackagesByUrl is a hook that fetches snippets or snippet packages. It pulls any
 * necessary query information from the current URL and decides what to fetch based on the "grouping" param.
 */
export default function useSnippetsOrSnippetPackagesByUrl() {
  const routeParams = useRouteParams<SnippetsRouteParams>();
  const userRevisionLocator = `${routeParams.project_id}$${routeParams.revision_id}`;

  const [queryParams] = useQueryParams<SnippetsQueryParams>();

  const options: UseSnippetsOrSnippetPackagesOptions = {
    userRevisionLocator,
    params: omitClientOnlySnippetParams({ ...queryParams, path: queryParams.path ?? '/' }),
  };

  if (queryParams.olderRevisionLocator && routeParams.status) {
    options.compare = {
      olderRevisionLocator: queryParams.olderRevisionLocator,
      status: routeParams.status,
    };
  }

  const isSnippetGroupingEnabled = useIsFeatureEnabled(SNIPPET_GROUPING);
  const { snippetGrouping } = useSnippetGrouping();
  const isGroupingByPackage = isSnippetGroupingEnabled && snippetGrouping === 'package';

  return useQuery<SnippetsResponse | SnippetPackagesResponse>({
    queryKey: getSnippetsOrSnippetPackagesQueryKey(isGroupingByPackage, options),
    queryFn: () => isGroupingByPackage ? getSnippetPackages(options) : getSnippets(options),
    //  Path must exist in the request, so don't fetch if we don't have a path.
    enabled: !_.isNil(options.params?.path),
  });
}
